"use client";

import { useEffect } from "react";

/** Gap kept between the panel and the edge it rests against, either way up. */
const GAP_PX = 16;

/**
 * How far down the viewport this element holds the page, once pinned — 0 if it
 * does not hold it at all.
 *
 * ONLY CHROME THAT STAYS COUNTS. A `fixed` or `sticky` box keeps its place
 * while the page scrolls, so anything pinning beneath it owes it that space.
 * An IN-FLOW header owes nothing: it scrolls away, and reserving its height
 * leaves a permanent gap under the top of the screen.
 *
 * Not hypothetical. The Header & Footer Builder's applied header is
 * `position: relative` and `site-chrome.tsx` swaps it in for the standard
 * fixed `<Nav>` app-wide — its own `mainSpacing` says "An APPLIED header is in
 * flow and owes nothing." Measuring every `<header>` published that header's
 * full height as sticky chrome for those tenants, once and for the session.
 *
 * PINNED position, not current position, and that is the other half. A
 * `sticky` box that has not been reached yet sits lower than where it will
 * come to rest: the notice bar under an applied header is `sticky top-0`
 * (`notice-bar.tsx`), so at the top of the page its edge includes the header
 * above it and shrinks to its own height once the header scrolls past.
 * `top + height` is where it ends up, which is the number anything pinning
 * below it needs — and it does not change with scroll, so this needs no
 * scroll listener on a page held to a strict interaction budget.
 *
 * `top: auto` means the box is not anchored to the top edge at all (a footer
 * bar), so it reserves nothing here.
 */
function pinnedBottom(el: HTMLElement): number {
  const style = getComputedStyle(el);
  if (style.position !== "fixed" && style.position !== "sticky") return 0;
  const top = Number.parseFloat(style.top);
  if (!Number.isFinite(top)) return 0;
  // A dismissed notice bar is `display: none` and so has no height. It keeps
  // its `position` and `top`, so without this it would still reserve its
  // offset — the 40px the dismissal was supposed to give back.
  const { height } = el.getBoundingClientRect();
  return height > 0 ? top + height : 0;
}

/**
 * Every number this component publishes, read in one pass.
 *
 * Module scope, and EVERY READ HAPPENS HERE before the caller writes anything.
 * Interleaved, each write invalidates style and the next
 * `getBoundingClientRect` has to force a synchronous layout to answer — a
 * read/write/read/write chain on the interaction path the `<details>` facets
 * were chosen to keep cheap.
 */
function measureOffsets(panel: HTMLElement | null): {
  chrome: number;
  barHeight: number;
  /** The panel's resting top — null when this tenant has no filter panel. */
  top: number | null;
} {
  const chrome = [
    document.querySelector<HTMLElement>("header"),
    document.getElementById("tc-notice-bar"),
  ]
    .filter((el): el is HTMLElement => el !== null)
    .reduce((max, el) => Math.max(max, pinnedBottom(el)), 0);

  // The mobile filter bar pins UNDER the chrome, so anything scrolling to a
  // position has to clear both. The bar is `lg:hidden`, so above `lg` it
  // measures 0 on its own and one expression serves both breakpoints with no
  // media query to keep in sync. `offsetHeight` (not a rect) because a
  // `display: none` element reports 0 without the caller testing for it.
  const barHeight =
    document.querySelector<HTMLElement>("[data-mobile-filter-bar]")
      ?.offsetHeight ?? 0;

  if (panel === null) return { chrome, barHeight, top: null };

  const fits = chrome + GAP_PX;
  const pinnedByBottom =
    window.innerHeight - panel.getBoundingClientRect().height - GAP_PX;

  return { chrome, barHeight, top: Math.min(fits, pinnedByBottom) };
}

/**
 * Publishes the filter panel's resting `top` as `--tc-filter-top`, plus the
 * two chrome offsets anything else on this page pins against.
 *
 * **Why a measured number and not a constant.** `position: sticky` can only
 * hold a box by its `top` edge. A panel TALLER than the viewport therefore pins
 * with its lower half past the fold, permanently unreachable — which is why the
 * panel used to cap its height and grow a second scrollbar inside the page.
 * `bottom` is not the escape hatch it looks like: it pulls a box UP into view
 * from below, it does not hold one that is scrolling up and out, so it produced
 * no offset at all here (measured, 2026-09-23).
 *
 * The one offset that does produce "scroll through the filters, then rest with
 * the last one at the bottom of the screen" is a NEGATIVE top of
 * `viewport - panelHeight`. Both terms are runtime facts: the panel's height
 * changes with the tenant's facet counts and with every "Show more" click, and
 * the chrome above it is a nav plus a notice bar that is dismissible, may wrap
 * to two lines, and on some tenants is not fixed at all (see `pinnedBottom`).
 * A hardcoded `top-20` was wrong about the second one by 24px, which is what
 * hid the panel's own header behind the notice bar.
 *
 * So: rest below the chrome while the panel fits, and by its bottom edge once it
 * does not.
 *
 * Same mechanism as `--tc-tour-marker` in `_tour-tabs-bar.client.tsx` — a
 * `ResizeObserver` on the element itself, which fires for a font swap too.
 */
export function FilterStickyTop(): null {
  useEffect(() => {
    // NO EARLY RETURN WHEN THERE IS NO PANEL. Two of the three properties are
    // not the panel's at all — `--tc-chrome-bottom` positions the mobile
    // filter bar and `--tc-filter-bar-h` feeds the results anchor's
    // scroll-margin — so bailing left an operator who switched the filter bar
    // off with neither published, and the anchor silently falling back to its
    // CSS default. This component mounts on the tours index, panel or no
    // panel; `measureOffsets` returns a null `top` when there is nothing to
    // rest.
    const panel = document.querySelector<HTMLElement>("[data-filter-sidebar]");

    // Writes are SKIPPED when the value has not moved. A custom property set
    // on the root invalidates style for the whole document, and most of what
    // triggers a re-measure moves only one of these three: opening a facet
    // changes the panel's height and nothing about the chrome, so re-writing
    // the chrome's two properties every frame of a 220ms animation costs a
    // document-wide invalidation each time and changes nothing.
    // Compared against the DOM, not against a remembered value. A private
    // cache goes stale the moment anything else clears the property — a second
    // instance unmounting during an overlapping client transition, say — and
    // then the skip is silent: the Map still claims the value is set, the next
    // matching publish writes nothing, and the panel and bar fall back to
    // their CSS defaults with nothing red anywhere. Reading the root's own
    // style is exact and costs no layout.
    const root = document.documentElement;
    const write = (name: string, value: string): void => {
      if (root.style.getPropertyValue(name) === value) return;
      root.style.setProperty(name, value);
    };

    const publish = (): void => {
      const { chrome, barHeight, top } = measureOffsets(panel);
      // The chrome's own bottom edge, for anything that simply pins UNDER it.
      // Published from here because this component already measures the chrome
      // every time it can change; a second observer for the same number is how
      // the two drift apart.
      write("--tc-chrome-bottom", `${Math.round(chrome)}px`);
      write("--tc-filter-bar-h", `${barHeight}px`);
      if (top !== null) write("--tc-filter-top", `${Math.round(top)}px`);
    };

    // COALESCED to one pass per frame. The facets animate their height over
    // 220ms, so the observer fires on roughly every frame of every open and
    // close — and several observed elements can report in the same frame.
    // Without this each report measured and wrote again inside one frame.
    let frame: number | null = null;
    const schedule = (): void => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        publish();
      });
    };

    // The FIRST pass is synchronous: deferring it to a frame would paint once
    // with the CSS fallbacks before the measured values land.
    publish();
    window.addEventListener("resize", schedule);

    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(schedule);
    if (panel) observer?.observe(panel);

    // THE CHROME IS OBSERVED TOO, not just the panel.
    //
    // Dismissing the notice bar is pure DOM — `notice-bar-dismiss.client.tsx`
    // sets `display: none` and strips `data-notice-below`, with no React state
    // behind it. Observing only the panel missed that entirely: hiding a
    // SIBLING changes the panel's position, never its size, so neither the
    // observer nor `resize` fired. Both offsets then stayed 40px too large for
    // the rest of the session, and the sticky mobile filter bar floated in a
    // gap under the nav that the results scrolled through.
    //
    // A hidden element reports a 0x0 box and that notifies a ResizeObserver, so
    // observing the bar itself is enough — no custom event, and no second
    // component that has to remember to tell this one.
    for (const el of [
      document.getElementById("tc-notice-bar"),
      document.querySelector<HTMLElement>("header"),
      document.querySelector<HTMLElement>("[data-mobile-filter-bar]"),
    ]) {
      if (el) observer?.observe(el);
    }

    return () => {
      window.removeEventListener("resize", schedule);
      observer?.disconnect();
      // A frame queued on the way out would run against an unmounted page and
      // re-write the properties this cleanup is removing.
      if (frame !== null) window.cancelAnimationFrame(frame);
      document.documentElement.style.removeProperty("--tc-filter-top");
      document.documentElement.style.removeProperty("--tc-chrome-bottom");
      document.documentElement.style.removeProperty("--tc-filter-bar-h");
    };
  }, []);

  return null;
}
